import React, { useEffect, useState, useCallback } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import {
  ShieldCheck, Users, Car, MessageSquare, Bell, Mail, CreditCard,
  Search, Eye, EyeOff, Trash2, UserX, UserCheck, Crown, AlertTriangle,
  RefreshCw, ChevronLeft, ChevronRight, ScrollText, Building2,
  Activity, BellRing, Smartphone, Send, Loader2, CheckCircle2, XCircle,
  GitCompareArrows, Flame, Swords, TrendingUp, MousePointerClick, Target, TrendingDown,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, Tooltip } from "recharts";

const TABS = [
  { id: "overview", label: "Übersicht", icon: ShieldCheck },
  { id: "users", label: "Benutzer", icon: Users },
  { id: "listings", label: "Inserate", icon: Car },
  { id: "searches", label: "Suchen", icon: Bell },
  { id: "messages", label: "Nachrichten", icon: MessageSquare },
  { id: "audit", label: "Audit-Log", icon: ScrollText },
  { id: "dealers", label: "Händler", icon: Building2 },
  { id: "push", label: "Push Analytics", icon: BellRing },
  { id: "compare", label: "Compare Analytics", icon: GitCompareArrows },
  { id: "email", label: "E-Mail", icon: Mail },
];

function fmtCHF(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", minimumFractionDigits: 0 }).format(n);
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}
function shortId(s) { return s ? `${s.slice(0, 6)}…${s.slice(-4)}` : ""; }

function StatCard({ icon: Icon, label, value, hint, accent = "red" }) {
  const accentMap = {
    red: "from-red-600/20 to-red-600/0 border-red-500/30 text-red-400",
    emerald: "from-emerald-600/15 to-emerald-600/0 border-emerald-500/25 text-emerald-400",
    sky: "from-sky-600/15 to-sky-600/0 border-sky-500/25 text-sky-400",
    amber: "from-amber-600/15 to-amber-600/0 border-amber-500/25 text-amber-400",
  };
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-zinc-950 p-5 ${accentMap[accent]?.split(" ").slice(2, 3) || "border-zinc-800"}`}>
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accentMap[accent]?.split(" ").slice(0, 2).join(" ") || ""}`} />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
          <p className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl">{value}</p>
          {hint && <p className="mt-1 text-xs text-zinc-400">{hint}</p>}
        </div>
        {Icon && <Icon className={`h-5 w-5 ${accentMap[accent]?.split(" ").slice(3).join(" ") || "text-zinc-500"}`} />}
      </div>
    </div>
  );
}

function Pager({ page, total, limit, onPage }) {
  const last = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="mt-4 flex items-center justify-between text-xs text-zinc-400">
      <span>Seite {page} / {last} · {total} Einträge</span>
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1}
          className="h-8 border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onPage(Math.min(last, page + 1))} disabled={page >= last}
          className="h-8 border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ---- Overview Tab ----
function OverviewTab({ stats, loading, onRefresh }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">{stats?.generated_at ? `Aktualisiert: ${fmtDate(stats.generated_at)}` : ""}</p>
        <Button onClick={onRefresh} disabled={loading} variant="outline" size="sm"
          className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900" data-testid="admin-refresh-stats">
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
        </Button>
      </div>
      {!stats ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl border border-zinc-800 bg-zinc-950" />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard icon={Users} label="Benutzer" value={stats.users.total} hint={`${stats.users.new_7d} neu (7T) · ${stats.users.suspended} gesperrt`} accent="sky" />
            <StatCard icon={Car} label="Inserate aktiv" value={stats.listings.active} hint={`${stats.listings.total} gesamt · ${stats.listings.new_7d} neu (7T)`} accent="red" />
            <StatCard icon={Crown} label="Boost aktiv" value={stats.listings.boosted_active} hint={`${stats.listings.hidden} versteckt`} accent="amber" />
            <StatCard icon={CreditCard} label="Umsatz gesamt" value={fmtCHF(stats.revenue.total)} hint={`${stats.revenue.transactions_total} Transaktionen`} accent="emerald" />
            <StatCard icon={CreditCard} label="Umsatz 7 Tage" value={fmtCHF(stats.revenue.last_7d)} hint={`${stats.revenue.transactions_7d} Transaktionen`} accent="emerald" />
            <StatCard icon={MessageSquare} label="Nachrichten" value={stats.messages.total} hint={`${stats.messages.last_24h} in 24h`} accent="sky" />
            <StatCard icon={Bell} label="Saved Searches" value={stats.saved_searches.total} hint={`${stats.saved_searches.active} aktiv`} accent="amber" />
            <StatCard icon={Mail} label="E-Mails 24h" value={stats.email.password_resets_24h + stats.email.email_change_24h + stats.email.saved_search_alerts_24h}
              hint={`${stats.email.password_resets_24h} Reset · ${stats.email.email_change_24h} Mailchange · ${stats.email.saved_search_alerts_24h} Alerts`} accent="red" />
          </div>
        </>
      )}
    </div>
  );
}

// ---- Users Tab ----
function UsersTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1, query = q) => {
    setLoading(true);
    try {
      const res = await api.adminUsers({ q: query || undefined, page, limit: 20 });
      setData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(1, ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onSuspend = async (u) => {
    const reason = window.prompt(`Benutzer "${u.email}" sperren? Optionaler Grund:`, "");
    if (reason === null) return;
    try { await api.adminSuspendUser(u.id, reason); toast.success("Benutzer gesperrt"); load(data.page, q); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const onUnsuspend = async (u) => {
    if (!window.confirm(`Benutzer "${u.email}" entsperren?`)) return;
    try { await api.adminUnsuspendUser(u.id); toast.success("Entsperrt"); load(data.page, q); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); load(1, q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche nach E-Mail oder Name…"
            className="border-zinc-800 bg-black pl-9 text-white" data-testid="admin-users-search" />
        </div>
        <Button type="submit" className="bg-red-600 hover:bg-red-500">Suchen</Button>
      </form>

      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-32 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine Benutzer gefunden.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((u) => (
              <li key={u.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-user-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-sm font-semibold text-white sm:text-base">{u.name || "(ohne Name)"}</p>
                    {u.role === "admin" && <span className="rounded-full bg-red-600/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-400">Admin</span>}
                    {u.suspended && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Gesperrt</span>}
                  </div>
                  <p className="mt-0.5 break-all font-mono text-xs text-zinc-400">{u.email}</p>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    {fmtDate(u.created_at)} · {u.listings_count || 0} Inserate · {u.preferred_locale || "—"}
                    {u.suspend_reason && ` · Grund: ${u.suspend_reason}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {u.role !== "admin" && (
                    u.suspended ? (
                      <Button size="sm" onClick={() => onUnsuspend(u)} variant="outline"
                        className="border-emerald-500/30 bg-transparent text-emerald-400 hover:bg-emerald-500/10"
                        data-testid={`admin-unsuspend-${u.id}`}>
                        <UserCheck className="mr-1.5 h-3.5 w-3.5" /> Entsperren
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => onSuspend(u)} variant="outline"
                        className="border-amber-500/30 bg-transparent text-amber-400 hover:bg-amber-500/10"
                        data-testid={`admin-suspend-${u.id}`}>
                        <UserX className="mr-1.5 h-3.5 w-3.5" /> Sperren
                      </Button>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={(p) => load(p, q)} />
    </div>
  );
}

// ---- Listings Tab ----
function ListingsTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1, query = q) => {
    setLoading(true);
    try {
      const res = await api.adminListings({ q: query || undefined, page, limit: 20 });
      setData(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(1, ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onHide = async (l) => {
    const reason = window.prompt(`Inserat "${l.title}" verstecken? Grund (Pflicht — wird per E-Mail an den Besitzer geschickt):`, "");
    if (!reason || !reason.trim()) return;
    try {
      const res = await api.adminHideListing(l.id, reason.trim());
      if (res?.notification_sent) {
        toast.success(`Versteckt · E-Mail an Besitzer gesendet${res.notification_mocked ? " (mock)" : ""}`);
      } else if (res?.notification_error) {
        toast.warning(`Versteckt — E-Mail nicht gesendet: ${res.notification_error}`);
      } else {
        toast.success("Versteckt");
      }
      load(data.page, q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };
  const onUnhide = async (l) => {
    if (!window.confirm(`"${l.title}" wieder sichtbar machen?`)) return;
    try { await api.adminUnhideListing(l.id); toast.success("Sichtbar"); load(data.page, q); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const onDelete = async (l) => {
    const reason = window.prompt(`⚠️  Inserat "${l.title}" PERMANENT löschen? Grund (Pflicht — wird per E-Mail an den Besitzer geschickt):`, "");
    if (!reason || !reason.trim()) return;
    if (!window.confirm("Wirklich permanent löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) return;
    try {
      const res = await api.adminDeleteListing(l.id, reason.trim());
      if (res?.notification_sent) {
        toast.success(`Inserat gelöscht · E-Mail an Besitzer gesendet${res.notification_mocked ? " (mock)" : ""}`);
      } else if (res?.notification_error) {
        toast.warning(`Inserat gelöscht — E-Mail nicht gesendet: ${res.notification_error}`);
      } else {
        toast.success("Inserat gelöscht");
      }
      load(data.page, q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={(e) => { e.preventDefault(); load(1, q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche Titel, Marke, Modell…"
            className="border-zinc-800 bg-black pl-9 text-white" data-testid="admin-listings-search" />
        </div>
        <Button type="submit" className="bg-red-600 hover:bg-red-500">Suchen</Button>
      </form>

      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-32 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine Inserate gefunden.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((l) => (
              <li key={l.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-listing-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/listings/${l.id}`} className="font-display text-sm font-semibold text-white hover:text-red-400 sm:text-base">
                      {l.title}
                    </Link>
                    {l.hidden && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Versteckt</span>}
                    {l.hidden && l.hidden_notification_sent && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400"
                        title={l.hidden_notification_at ? `Gesendet: ${fmtDate(l.hidden_notification_at)}` : ""}
                        data-testid={`admin-listing-${l.id}-mail-sent`}
                      >
                        <Mail className="h-3 w-3" /> E-Mail
                      </span>
                    )}
                    {l.featured && <span className="rounded-full bg-red-600/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-400">Boost</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-400">
                    {fmtCHF(l.price_chf)} · {l.brand} {l.model} · {l.year} · {l.mileage_km?.toLocaleString("de-CH")} km
                  </p>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    {fmtDate(l.created_at)} · Owner: {shortId(l.owner_id)} · {l.views || 0} Aufrufe
                    {l.hidden_reason && ` · Grund: ${l.hidden_reason}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {l.hidden ? (
                    <Button size="sm" onClick={() => onUnhide(l)} variant="outline"
                      className="border-emerald-500/30 bg-transparent text-emerald-400 hover:bg-emerald-500/10"
                      data-testid={`admin-unhide-${l.id}`}>
                      <Eye className="mr-1.5 h-3.5 w-3.5" /> Sichtbar
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => onHide(l)} variant="outline"
                      className="border-amber-500/30 bg-transparent text-amber-400 hover:bg-amber-500/10"
                      data-testid={`admin-hide-${l.id}`}>
                      <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Verstecken
                    </Button>
                  )}
                  <Button size="sm" onClick={() => onDelete(l)} variant="outline"
                    className="border-red-500/30 bg-transparent text-red-400 hover:bg-red-500/10"
                    data-testid={`admin-delete-${l.id}`}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Löschen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={(p) => load(p, q)} />
    </div>
  );
}

// ---- Saved Searches Tab ----
function SearchesTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try { setData(await api.adminSavedSearches({ page, limit: 20 })); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-32 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine gespeicherten Suchen.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((s) => (
              <li key={s.id} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-sm font-semibold text-white">{s.name}</p>
                    <p className="mt-0.5 break-all text-xs text-zinc-400">{s.user_email || shortId(s.user_id)}</p>
                    <p className="mt-1 text-[11px] text-zinc-500">
                      {fmtDate(s.created_at)} · Frequenz: <span className="text-zinc-300">{s.email_frequency || "daily"}</span>
                      {s.last_notified_at && ` · Zuletzt: ${fmtDate(s.last_notified_at)}`}
                    </p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${s.notify ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-800 text-zinc-500"}`}>
                    {s.notify ? "Aktiv" : "Aus"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={load} />
    </div>
  );
}

// ---- Messages Tab ----
function MessagesTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try { setData(await api.adminMessages({ page, limit: 20 })); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, [load]);

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-32 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine Nachrichten.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((m) => (
              <li key={m.id} className="p-4">
                <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                  <span>{fmtDate(m.created_at)}</span>
                  <span>{shortId(m.sender_id)} → {shortId(m.recipient_id)}</span>
                </div>
                <p className="mt-2 line-clamp-2 text-sm text-zinc-200">{m.content}</p>
                {m.listing_id && (
                  <Link to={`/listings/${m.listing_id}`} className="mt-1 inline-block text-[11px] text-red-400 hover:text-red-300">
                    Inserat ansehen
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={load} />
    </div>
  );
}

// ---- Audit-Log Tab ----
const AUDIT_ACTIONS = [
  { id: "all", label: "Alle Aktionen", color: "zinc" },
  { id: "hide_listing", label: "Versteckt", color: "amber" },
  { id: "unhide_listing", label: "Sichtbar", color: "emerald" },
  { id: "delete_listing", label: "Gelöscht", color: "red" },
  { id: "suspend_user", label: "Gesperrt", color: "amber" },
  { id: "unsuspend_user", label: "Entsperrt", color: "emerald" },
];

function actionBadgeStyle(action) {
  const map = {
    hide_listing: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    unhide_listing: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    delete_listing: "bg-red-500/15 text-red-400 border-red-500/30",
    suspend_user: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    unsuspend_user: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  };
  return map[action] || "bg-zinc-800 text-zinc-300 border-zinc-700";
}
function actionLabel(action) {
  return AUDIT_ACTIONS.find((a) => a.id === action)?.label || action;
}
function actionIcon(action) {
  if (action === "hide_listing") return EyeOff;
  if (action === "unhide_listing") return Eye;
  if (action === "delete_listing") return Trash2;
  if (action === "suspend_user") return UserX;
  if (action === "unsuspend_user") return UserCheck;
  return ScrollText;
}

function AuditTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 30 });
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1, act = action, query = q) => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (act && act !== "all") params.action = act;
      if (query) params.q = query;
      setData(await api.adminAuditLog(params));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [action, q]);

  useEffect(() => { load(1, "all", ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onActionChange = (id) => {
    setAction(id);
    load(1, id, q);
  };

  return (
    <div className="space-y-4" data-testid="admin-audit-tab">
      {/* Filter chips + search */}
      <div className="space-y-3">
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2">
            {AUDIT_ACTIONS.map((a) => {
              const isActive = action === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => onActionChange(a.id)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    isActive
                      ? "border-red-500 bg-red-500/15 text-red-400"
                      : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                  }`}
                  data-testid={`admin-audit-filter-${a.id}`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(1, action, q); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche Titel, Email oder Grund…"
              className="border-zinc-800 bg-black pl-9 text-white" data-testid="admin-audit-search" />
          </div>
          <Button type="submit" className="bg-red-600 hover:bg-red-500">Suchen</Button>
        </form>
      </div>

      {/* Audit list */}
      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-48 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine Audit-Einträge gefunden.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((it) => {
              const ActIcon = actionIcon(it.action);
              const target = it.target_title || it.target_email || it.target_name || it.target_id;
              const isUserAction = it.action === "suspend_user" || it.action === "unsuspend_user";
              return (
                <li key={it.id} className="p-4" data-testid={`admin-audit-row-${it.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${actionBadgeStyle(it.action)}`}>
                      <ActIcon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${actionBadgeStyle(it.action)}`}>
                          {actionLabel(it.action)}
                        </span>
                        <span className="font-display text-sm font-semibold text-white">{target || "—"}</span>
                        {(it.notification_sent || it.delete_notification_sent) && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                            <Mail className="h-3 w-3" /> E-Mail
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-zinc-500">
                        {fmtDate(it.at)} · Admin: {it.admin_email || shortId(it.admin_id)}
                        {it.action.endsWith("_listing") && it.target_id && (
                          isUserAction ? null : (
                            it.action !== "delete_listing" ? (
                              <> · <Link to={`/listings/${it.target_id}`} className="text-red-400 hover:text-red-300">Inserat</Link></>
                            ) : null
                          )
                        )}
                      </p>
                      {it.reason && (
                        <div className="mt-2 rounded-md border border-zinc-800 bg-black px-3 py-2 text-xs leading-relaxed text-zinc-300">
                          {it.reason}
                        </div>
                      )}
                      {it.notification_error || it.delete_notification_error ? (
                        <p className="mt-1 text-[11px] text-red-400">⚠ E-Mail-Fehler: {it.notification_error || it.delete_notification_error}</p>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={(p) => load(p, action, q)} />
    </div>
  );
}

// ---- Admin Dealers Tab ----
function DealersTab() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [filter, setFilter] = useState("all"); // all | verified | unverified
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (page = 1, fl = filter, query = q) => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (fl === "verified") params.verified = true;
      if (fl === "unverified") params.verified = false;
      if (query) params.q = query;
      setData(await api.adminListDealers(params));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { load(1, "all", ""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onVerify = async (d, level = "standard") => {
    if (!window.confirm(`Dealer "${d.company_name || d.email}" als ${level === "premium" ? "PREMIUM-" : ""}verifiziert markieren?`)) return;
    try { await api.adminVerifyDealer(d.id, level); toast.success(`Verifiziert (${level})`); load(data.page, filter, q); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };
  const onUnverify = async (d) => {
    if (!window.confirm(`Verifikation für "${d.company_name || d.email}" entfernen?`)) return;
    try { await api.adminUnverifyDealer(d.id); toast.success("Verifikation entfernt"); load(data.page, filter, q); }
    catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="space-y-4">
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-2">
          {[
            { id: "all", label: "Alle" },
            { id: "verified", label: "Verifiziert" },
            { id: "unverified", label: "Ausstehend" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => { setFilter(f.id); load(1, f.id, q); }}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                filter === f.id
                  ? "border-red-500 bg-red-500/15 text-red-400"
                  : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
              }`}
              data-testid={`admin-dealers-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); load(1, filter, q); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche Firma, Slug, Email…"
            className="border-zinc-800 bg-black pl-9 text-white" data-testid="admin-dealers-search" />
        </div>
        <Button type="submit" className="bg-red-600 hover:bg-red-500">Suchen</Button>
      </form>

      <div className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
        {loading ? (
          <div className="h-32 animate-pulse" />
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500">Keine Händler gefunden.</div>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {data.items.map((d) => (
              <li key={d.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-dealer-${d.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-sm font-semibold text-white sm:text-base">
                      {d.company_name || "(ohne Firmenname)"}
                    </p>
                    {d.dealer_verified ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-600/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-300">
                        <ShieldCheck className="h-3 w-3" /> Verifiziert
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">
                        Ausstehend
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 break-all font-mono text-xs text-zinc-400">{d.email}</p>
                  <p className="mt-1 text-[11px] text-zinc-500">
                    {d.slug ? <>Slug: <span className="text-zinc-300">{d.slug}</span></> : <span className="text-amber-400">⚠ Kein Slug</span>}
                    {d.canton && ` · ${d.canton}`}
                    {d.since_year && ` · seit ${d.since_year}`}
                    {d.dealer_activated_at && ` · seit ${fmtDate(d.dealer_activated_at)}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {d.slug && (
                    <Button asChild size="sm" variant="outline"
                      className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
                      <Link to={`/dealers/${d.slug}`} target="_blank">Vorschau</Link>
                    </Button>
                  )}
                  {d.dealer_verified ? (
                    <Button size="sm" onClick={() => onUnverify(d)} variant="outline"
                      className="border-amber-500/30 bg-transparent text-amber-400 hover:bg-amber-500/10"
                      data-testid={`admin-dealer-unverify-${d.id}`}>
                      Verifikation entfernen
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" onClick={() => onVerify(d, "standard")}
                        className="bg-red-600 hover:bg-red-500"
                        data-testid={`admin-dealer-verify-${d.id}`}>
                        <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Verifizieren
                      </Button>
                      <Button size="sm" onClick={() => onVerify(d, "premium")} variant="outline"
                        className="border-amber-400/40 bg-transparent text-amber-300 hover:bg-amber-500/10"
                        data-testid={`admin-dealer-verify-premium-${d.id}`}>
                        Premium
                      </Button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Pager page={data.page} total={data.total} limit={data.limit} onPage={(p) => load(p, filter, q)} />
    </div>
  );
}

// ---- Email Stats Tab ----
function EmailTab({ stats }) {
  if (!stats) return <div className="h-32 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <StatCard icon={Mail} label="Reset-Mails 24h" value={stats.email.password_resets_24h} hint="Forgot-Password Anfragen" accent="sky" />
      <StatCard icon={Mail} label="Email-Change 24h" value={stats.email.email_change_24h} hint="Verifikations-Anfragen" accent="amber" />
      <StatCard icon={Bell} label="Saved-Search Alerts 24h" value={stats.email.saved_search_alerts_24h} hint="Cron-getriggert" accent="emerald" />
    </div>
  );
}

// ---- Push Analytics Tab ----
function fmtNum(n) { return new Intl.NumberFormat("de-CH").format(n || 0); }

function HealthBadge({ status }) {
  const map = {
    healthy: { cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300", icon: CheckCircle2, label: "Healthy" },
    warning: { cls: "bg-amber-500/15 border-amber-500/40 text-amber-300", icon: AlertTriangle, label: "Warnung" },
    error: { cls: "bg-red-500/15 border-red-500/40 text-red-300", icon: XCircle, label: "Fehler" },
  };
  const m = map[status] || map.warning;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${m.cls}`} data-testid={`push-health-${status}`}>
      <Icon className="h-3.5 w-3.5" /> {m.label}
    </span>
  );
}

function MiniTrend({ data, color, height = 64, testId }) {
  const series = (data || []).map((v, i) => ({ d: i, v: v || 0 }));
  if (!series.some((p) => p.v > 0)) {
    return (
      <div className="flex items-center justify-center text-[10px] text-zinc-600" style={{ height }} data-testid={testId}>
        keine Daten
      </div>
    );
  }
  const gradId = `pushtrend-${color.replace("#", "")}-${testId || "x"}`;
  return (
    <div style={{ height }} data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.4} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            cursor={{ stroke: color, strokeOpacity: 0.4, strokeWidth: 1 }}
            contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, color: "#fff", fontSize: 11 }}
            labelFormatter={() => ""}
            formatter={(v) => [fmtNum(v), ""]}
          />
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gradId})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PushKpi({ icon: Icon, label, value, hint, accent = "red", testId }) {
  const accents = {
    red: "bg-red-600/15 text-red-400",
    emerald: "bg-emerald-600/15 text-emerald-400",
    amber: "bg-amber-500/15 text-amber-400",
    sky: "bg-sky-600/15 text-sky-400",
    zinc: "bg-zinc-700/40 text-zinc-300",
  };
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${accents[accent] || accents.red}`}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
      </div>
      <p className="mt-2 font-display text-2xl font-bold text-white sm:text-3xl" data-testid={`${testId}-value`}>{value}</p>
      {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );
}

function PlatformBar({ platforms }) {
  const entries = Object.entries(platforms || {}).filter(([, n]) => n > 0);
  const total = entries.reduce((acc, [, n]) => acc + n, 0);
  if (total === 0) {
    return <p className="text-[12px] text-zinc-500">Noch keine Subscriptions.</p>;
  }
  const colorMap = {
    android: "bg-emerald-500",
    ios: "bg-sky-400",
    windows: "bg-blue-400",
    firefox: "bg-orange-400",
    other: "bg-zinc-500",
  };
  const labelMap = {
    android: "Android",
    ios: "iOS",
    windows: "Windows",
    firefox: "Firefox",
    other: "Andere",
  };
  return (
    <div>
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-zinc-900" data-testid="platform-bar">
        {entries.map(([k, n]) => (
          <div key={k} className={`${colorMap[k] || "bg-zinc-500"}`} style={{ width: `${(n / total) * 100}%` }} title={`${labelMap[k] || k}: ${n}`} />
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-5">
        {entries.map(([k, n]) => (
          <li key={k} className="flex items-center gap-1.5 text-zinc-300" data-testid={`platform-row-${k}`}>
            <span className={`h-2 w-2 rounded-full ${colorMap[k] || "bg-zinc-500"}`} />
            <span className="truncate">{labelMap[k] || k}</span>
            <span className="ml-auto font-semibold text-white">{fmtNum(n)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PushTab() {
  const [data, setData] = useState(null);
  const [ctr, setCtr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testBusy, setTestBusy] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.adminPushStats().catch(() => null),
      api.adminPushCtr(7).catch(() => null),
    ])
      .then(([stats, ctrData]) => {
        setData(stats);
        setCtr(ctrData);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const sendTestPush = async () => {
    setTestBusy(true);
    try {
      const r = await api.adminPushTest({ title: "CarHive Admin Test", body: "Push-Infrastruktur funktioniert ✅" });
      if (r?.delivered > 0) toast.success(`Test-Push: ${r.delivered}/${r.total} zugestellt`);
      else toast.warning("Kein Push zugestellt — prüfe deine eigenen Subscriptions.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test-Push fehlgeschlagen";
      toast.error(typeof msg === "string" ? msg : "Test-Push fehlgeschlagen");
    } finally {
      setTestBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-3" data-testid="admin-push-tab-loading">
        <div className="h-9 w-40 animate-pulse rounded bg-zinc-900" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />)}
        </div>
      </div>
    );
  }

  const t = data.totals;
  const tr = data.trend;
  const failureRate = (t.sent_7d + t.failed_7d) > 0
    ? Math.round((t.failed_7d / (t.sent_7d + t.failed_7d)) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-push-tab">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Push Analytics</h2>
          <HealthBadge status={data.health} />
          {!data.vapid_configured && (
            <span className="rounded-full border border-red-500/40 bg-red-500/15 px-2.5 py-1 text-[11px] font-semibold text-red-300">VAPID nicht konfiguriert</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reload} className="border-zinc-800 text-white hover:bg-zinc-900" data-testid="push-refresh">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Aktualisieren
          </Button>
          <Button size="sm" onClick={sendTestPush} disabled={testBusy} className="bg-red-600 hover:bg-red-500" data-testid="push-test-btn">
            {testBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />} Test-Push senden
          </Button>
        </div>
      </div>

      {/* Primary KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PushKpi icon={BellRing} label="Aktive Subs" value={fmtNum(t.active_subscriptions)} hint={`${fmtNum(t.users_with_push)} User`} accent="red" testId="kpi-push-subs" />
        <PushKpi icon={Send} label="Pushes · 24h" value={fmtNum(t.sent_24h)} hint="Erfolgreiche Sends" accent="emerald" testId="kpi-push-24h" />
        <PushKpi icon={Activity} label="Pushes · 7T" value={fmtNum(t.sent_7d)} hint={`+${fmtNum(t.new_subs_7d)} neue Subs`} accent="sky" testId="kpi-push-7d" />
        <PushKpi
          icon={t.failed_7d > 0 ? AlertTriangle : CheckCircle2}
          label="Fehler · 7T"
          value={`${fmtNum(t.failed_7d)}${t.sent_7d + t.failed_7d > 0 ? ` · ${failureRate}%` : ""}`}
          hint={t.rate_limited_7d ? `${fmtNum(t.rate_limited_7d)} rate-limited` : "Keine Fehler"}
          accent={failureRate > 25 ? "amber" : "zinc"}
          testId="kpi-push-failed"
        />
      </div>

      {/* Trends row */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Sends · 7 Tage</p>
            <span className="text-[10px] text-zinc-600">{tr.days?.[0]} – {tr.days?.[tr.days.length - 1]}</span>
          </div>
          <p className="mt-1 font-display text-xl font-bold text-white">{fmtNum(t.sent_7d)}</p>
          <div className="mt-3">
            <MiniTrend data={tr.sent} color="#10b981" height={70} testId="trend-sent" />
          </div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Neue Subs · 7 Tage</p>
          <p className="mt-1 font-display text-xl font-bold text-white">{fmtNum(t.new_subs_7d)}</p>
          <div className="mt-3">
            <MiniTrend data={tr.subscribe} color="#dc2626" height={70} testId="trend-subscribe" />
          </div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Fehler · 7 Tage</p>
          <p className="mt-1 font-display text-xl font-bold text-white">{fmtNum(t.failed_7d)}</p>
          <div className="mt-3">
            <MiniTrend data={tr.failed} color="#f59e0b" height={70} testId="trend-failed" />
          </div>
        </div>
      </div>

      {/* Platform split + Secondary stats */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-zinc-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Plattformen</p>
          </div>
          <div className="mt-3">
            <PlatformBar platforms={data.platforms} />
          </div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Saved Searches mit Push</p>
          <p className="mt-2 font-display text-2xl font-bold text-white">{fmtNum(t.searches_push_enabled)}</p>
          <ul className="mt-3 space-y-1.5 text-[12px] text-zinc-400">
            <li className="flex justify-between"><span>Skipped: keine Subs</span><span className="font-semibold text-zinc-200">{fmtNum(t.no_subs_7d)}</span></li>
            <li className="flex justify-between"><span>Skipped: Rate-Limit</span><span className="font-semibold text-zinc-200">{fmtNum(t.rate_limited_7d)}</span></li>
            <li className="flex justify-between"><span>Unsubscribes · 7T</span><span className="font-semibold text-zinc-200">{fmtNum(t.unsubs_7d)}</span></li>
          </ul>
        </div>
      </div>

      {/* Phase 51 — CTR & Funnel */}
      <CtrFunnelSection ctr={ctr} />
    </div>
  );
}

// ---- Phase 51: CTR & Funnel section (Push → Click → Listing-View) ----
function CtrFunnelSection({ ctr }) {
  if (!ctr) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5" data-testid="push-ctr-empty">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">CTR & Funnel</p>
        <p className="mt-2 text-[12px] text-zinc-500">Daten werden geladen oder es liegen noch keine Tracking-Events vor.</p>
      </div>
    );
  }
  const t = ctr.totals || {};
  const trend = ctr.trend || {};
  return (
    <div className="space-y-4" data-testid="push-ctr-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MousePointerClick className="h-4 w-4 text-red-500" />
          <h3 className="font-display text-base font-semibold text-white sm:text-lg">CTR & Funnel · {ctr.days || 7} Tage</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Push → Click → Listing-View</span>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PushKpi icon={Send} label="Sent · 7T" value={fmtNum(t.sent)} hint="Erfolgreich zugestellt" accent="sky" testId="kpi-ctr-sent" />
        <PushKpi icon={MousePointerClick} label="Clicked" value={fmtNum(t.clicked)} hint={`${t.ctr_pct ?? 0}% CTR`} accent="red" testId="kpi-ctr-clicked" />
        <PushKpi icon={Target} label="CTR" value={`${t.ctr_pct ?? 0}%`} hint={t.sent ? `${t.clicked}/${t.sent} Klicks` : "—"} accent="emerald" testId="kpi-ctr-rate" />
        <PushKpi icon={Eye} label="Listing-Views" value={fmtNum(t.listing_opened)} hint={`${t.view_rate_pct ?? 0}% von Klicks · ${t.funnel_pct ?? 0}% Funnel`} accent="amber" testId="kpi-ctr-views" />
      </div>

      {/* CTR sparkline (full width) */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid="push-ctr-trend">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">CTR-Verlauf · {ctr.days || 7} Tage</p>
          <span className="text-[10px] text-zinc-600">{trend.days?.[0]} – {trend.days?.[trend.days?.length - 1]}</span>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <MiniTrend data={trend.ctr_pct} color="#dc2626" height={90} testId="trend-ctr" />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[11px] text-zinc-400 sm:grid-cols-1">
            <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-black/40 px-3 py-2">
              <span>Sends/Tag · Ø</span>
              <span className="font-semibold text-white">{trend.sent?.length ? Math.round(trend.sent.reduce((a, b) => a + b, 0) / trend.sent.length) : 0}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-black/40 px-3 py-2">
              <span>Clicks/Tag · Ø</span>
              <span className="font-semibold text-white">{trend.clicked?.length ? Math.round(trend.clicked.reduce((a, b) => a + b, 0) / trend.clicked.length) : 0}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-zinc-800 bg-black/40 px-3 py-2">
              <span>Views/Tag · Ø</span>
              <span className="font-semibold text-white">{trend.listing_opened?.length ? Math.round(trend.listing_opened.reduce((a, b) => a + b, 0) / trend.listing_opened.length) : 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Per-search table */}
      <CtrTable
        title="Top Saved Searches · nach CTR"
        rows={ctr.per_search || []}
        columns={[
          { key: "name", label: "Saved Search", render: (r) => (<span className="truncate text-white">{r.name}{r.filters_brand ? <span className="ml-1.5 text-[10px] text-zinc-500">{r.filters_brand}</span> : null}</span>) },
          { key: "sent", label: "Sent", render: (r) => fmtNum(r.sent) },
          { key: "clicked", label: "Click", render: (r) => fmtNum(r.clicked) },
          { key: "ctr_pct", label: "CTR", render: (r) => <span className={r.ctr_pct >= 20 ? "text-emerald-400 font-semibold" : r.ctr_pct >= 5 ? "text-zinc-200" : "text-zinc-500"}>{r.ctr_pct}%</span> },
        ]}
        emptyLabel="Noch keine Saved-Search-Pushes."
        testId="push-ctr-per-search"
      />

      {/* Brand CTR best/worst */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <CtrTable
          title="Beste CTR · nach Marke"
          rows={ctr.per_brand_best || []}
          icon={TrendingUp}
          accent="emerald"
          columns={[
            { key: "brand", label: "Marke", render: (r) => <span className="text-white">{r.brand}</span> },
            { key: "sent", label: "Sent", render: (r) => fmtNum(r.sent) },
            { key: "clicked", label: "Click", render: (r) => fmtNum(r.clicked) },
            { key: "ctr_pct", label: "CTR", render: (r) => <span className="font-semibold text-emerald-400">{r.ctr_pct}%</span> },
          ]}
          emptyLabel="Noch keine Markendaten."
          testId="push-ctr-brand-best"
        />
        <CtrTable
          title="Schlechteste CTR · nach Marke"
          rows={ctr.per_brand_worst || []}
          icon={TrendingDown}
          accent="amber"
          columns={[
            { key: "brand", label: "Marke", render: (r) => <span className="text-white">{r.brand}</span> },
            { key: "sent", label: "Sent", render: (r) => fmtNum(r.sent) },
            { key: "clicked", label: "Click", render: (r) => fmtNum(r.clicked) },
            { key: "ctr_pct", label: "CTR", render: (r) => <span className={r.ctr_pct === 0 ? "font-semibold text-red-400" : "font-semibold text-amber-300"}>{r.ctr_pct}%</span> },
          ]}
          emptyLabel="Keine schwachen Marken."
          testId="push-ctr-brand-worst"
        />
      </div>

      {/* Zero-engagement saved searches */}
      {ctr.zero_engagement && ctr.zero_engagement.length > 0 && (
        <div className="rounded-lg border border-red-500/30 bg-red-950/15 p-4 sm:p-5" data-testid="push-ctr-zero-engagement">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-red-300">Pushes ohne Engagement (≥3 Sends · 0 Klicks)</p>
          </div>
          <ul className="mt-3 space-y-1.5 text-[12px]">
            {ctr.zero_engagement.map((r) => (
              <li key={r.search_id} className="flex items-center justify-between rounded-md border border-red-500/20 bg-black/40 px-3 py-2" data-testid={`push-ctr-zero-row-${r.search_id}`}>
                <span className="truncate text-white">{r.name}{r.filters_brand ? <span className="ml-1.5 text-[10px] text-zinc-500">{r.filters_brand}</span> : null}</span>
                <span className="text-[11px] text-zinc-400">{r.sent} Sends · <span className="font-semibold text-red-300">0% CTR</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Top performing pushes by brand+model */}
      {ctr.top_pushes && ctr.top_pushes.length > 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid="push-ctr-top-pushes">
          <div className="flex items-center gap-2">
            <Flame className="h-4 w-4 text-red-500" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Top-Pushes nach Clicks</p>
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {ctr.top_pushes.map((p, i) => (
              <li key={i} className="flex items-center justify-between rounded-md border border-zinc-800 bg-black/40 px-3 py-2 text-[12px]" data-testid={`push-ctr-top-row-${i}`}>
                <span className="truncate text-white">{p.brand} <span className="text-zinc-500">{p.model || "—"}</span></span>
                <span className="font-semibold text-red-400">{p.clicked} Clicks</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CtrTable({ title, rows, columns, emptyLabel, icon: Icon, accent = "zinc", testId }) {
  const iconColor = {
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    red: "text-red-400",
    zinc: "text-zinc-400",
  }[accent] || "text-zinc-400";
  if (!rows || rows.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid={testId}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{title}</p>
        <p className="mt-2 text-[12px] text-zinc-500">{emptyLabel}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-4 w-4 ${iconColor}`} />}
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{title}</p>
      </div>
      <div className="mt-3 overflow-hidden rounded-md border border-zinc-800/70">
        <table className="w-full text-[12px]">
          <thead className="bg-black/50 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-left">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 10).map((r, i) => (
              <tr key={r.search_id || r.brand || i} className="border-t border-zinc-900 hover:bg-black/30" data-testid={`${testId}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-zinc-300">{c.render(r)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Compare Analytics Tab ----
function fmtCHFShort(n) {
  if (n == null) return "—";
  if (n >= 1000) return `CHF ${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}k`;
  return `CHF ${n}`;
}

function InsightBadge({ kind, label, value }) {
  const map = {
    trending: { cls: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300", Icon: Flame, prefix: "Trending" },
    competitive: { cls: "border-red-500/40 bg-red-600/15 text-red-300", Icon: Swords, prefix: "Most Competitive" },
    hot: { cls: "border-amber-500/40 bg-amber-500/15 text-amber-200", Icon: TrendingUp, prefix: "Hot" },
  };
  const m = map[kind] || map.hot;
  const I = m.Icon;
  return (
    <div className={`rounded-lg border ${m.cls} p-4`} data-testid={`compare-insight-${kind}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
        <I className="h-3 w-3" /> {m.prefix}
      </div>
      <p className="mt-2 line-clamp-1 font-display text-base font-bold text-white">{label || "—"}</p>
      {value && <p className="mt-0.5 text-[11px] opacity-80">{value}</p>}
    </div>
  );
}

function TopList({ title, items, formatValue, testId, emptyLabel = "Noch keine Daten" }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid={testId}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{title}</p>
        <p className="mt-3 text-[12px] text-zinc-500">{emptyLabel}</p>
      </div>
    );
  }
  const max = items[0]?.n || 1;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid={testId}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{title}</p>
      <ol className="mt-3 space-y-2.5">
        {items.slice(0, 10).map((it, idx) => (
          <li key={idx} className="group" data-testid={`${testId}-row-${idx}`}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex items-baseline gap-2 truncate text-sm text-zinc-200">
                <span className="w-5 shrink-0 text-[11px] font-mono text-zinc-500">#{idx + 1}</span>
                <span className="truncate">{it.label}</span>
              </span>
              <span className="shrink-0 text-[11px] font-semibold text-white">
                {formatValue ? formatValue(it) : it.n}
              </span>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-zinc-900">
              <div className="h-full bg-red-600 transition-all group-hover:bg-red-500" style={{ width: `${(it.n / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function TopListings({ items }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Most Compared Listings</p>
        <p className="mt-3 text-[12px] text-zinc-500">Noch keine Daten</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5" data-testid="compare-top-listings">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Most Compared Listings</p>
      <ul className="mt-3 divide-y divide-zinc-900">
        {items.map((it, idx) => (
          <li key={it.id} className="flex items-center gap-3 py-2.5" data-testid={`compare-top-listing-${idx}`}>
            <span className="w-5 shrink-0 text-center text-[11px] font-mono text-zinc-500">#{idx + 1}</span>
            <div className="h-9 w-12 shrink-0 overflow-hidden rounded bg-zinc-900">
              {it.image && <img src={api.fileUrl(it.image)} alt="" className="h-full w-full object-cover" />}
            </div>
            <div className="min-w-0 flex-1">
              <Link to={`/listings/${it.id}`} className="line-clamp-1 text-sm font-semibold text-white hover:text-red-300" data-testid={`compare-listing-link-${idx}`}>
                {it.brand} {it.model}
              </Link>
              <p className="text-[11px] text-zinc-500">
                {it.year ? `${it.year} · ` : ""}{fmtCHFShort(it.price_chf)}
                {!it.is_active && <span className="ml-1 rounded bg-zinc-800 px-1 py-0.5 text-[9px] text-zinc-400">inaktiv</span>}
              </p>
            </div>
            <span className="shrink-0 rounded-full bg-red-600/15 px-2.5 py-1 text-[11px] font-semibold text-red-300">{it.compare_count}×</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CompareTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    api.adminCompareStats()
      .then((r) => setData(r))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !data) {
    return (
      <div className="space-y-3" data-testid="admin-compare-tab-loading">
        <div className="h-9 w-44 animate-pulse rounded bg-zinc-900" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />)}
        </div>
      </div>
    );
  }

  const t = data.totals;
  const tr = data.trend;
  const ins = data.insights || {};
  const topTrending = (ins.trending_models || [])[0];

  return (
    <div className="space-y-6" data-testid="admin-compare-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-semibold text-white sm:text-2xl">Compare Analytics</h2>
        <Button variant="outline" size="sm" onClick={reload} className="border-zinc-800 text-white hover:bg-zinc-900" data-testid="compare-refresh">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Aktualisieren
        </Button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PushKpi icon={GitCompareArrows} label="Vergleiche · 24h" value={fmtNum(t.compares_24h)} accent="red" testId="kpi-cmp-24h" />
        <PushKpi icon={Activity} label="Vergleiche · 7T" value={fmtNum(t.compares_7d)} accent="emerald" testId="kpi-cmp-7d" />
        <PushKpi icon={TrendingUp} label="Vergleiche · 30T" value={fmtNum(t.compares_30d)} hint={`Ø ${t.avg_cars_per_compare} Cars/Compare`} accent="sky" testId="kpi-cmp-30d" />
        <PushKpi icon={Flame} label="Unique Modelle · 30T" value={fmtNum(t.unique_models_30d)} hint="Im Compare-Set" accent="amber" testId="kpi-cmp-models" />
      </div>

      {/* Trend chart */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400">Compare-Events · 30 Tage</p>
          <span className="text-[10px] text-zinc-600">{tr.days?.[0]} – {tr.days?.[tr.days.length - 1]}</span>
        </div>
        <p className="mt-1 font-display text-2xl font-bold text-white">{fmtNum(t.compares_30d)}</p>
        <div className="mt-3" data-testid="compare-trend-chart">
          <MiniTrend data={tr.compares} color="#dc2626" height={90} testId="trend-compares" />
        </div>
      </div>

      {/* Insight badges */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InsightBadge
          kind="trending"
          label={topTrending?.model}
          value={topTrending ? `${topTrending.recent}× in 7T (${topTrending.delta_pct >= 999 ? "neu" : `+${topTrending.delta_pct}%`})` : "Noch nicht genug Daten"}
        />
        <InsightBadge
          kind="competitive"
          label={ins.most_competitive_segment}
          value={data.top_pairs?.[0] ? `${data.top_pairs[0].n}× verglichen` : "Noch keine Daten"}
        />
        <InsightBadge
          kind="hot"
          label={data.top_models?.[0]?.label}
          value={data.top_models?.[0] ? `${data.top_models[0].n}× in 30T` : "Noch keine Daten"}
        />
      </div>

      {/* Top lists row */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TopList
          title="Top verglichene Modelle · 30T"
          items={data.top_models}
          testId="compare-top-models"
        />
        <TopList
          title="Top Brand-Versus · 30T"
          items={data.top_pairs}
          testId="compare-top-pairs"
        />
      </div>

      {/* Top brands + Top listings */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1.4fr]">
        <TopList
          title="Top Brands · 30T"
          items={data.top_brands}
          testId="compare-top-brands"
        />
        <TopListings items={data.top_listings} />
      </div>
    </div>
  );
}



// ---- Main Page ----
export default function Admin() {
  const { user } = useAuth();
  const navigate = useLocaleNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try { setStats(await api.adminStats()); }
    catch (err) {
      if (err?.response?.status === 403) toast.error("Kein Admin-Zugriff");
      else toast.error(err?.response?.data?.detail || "Fehler beim Laden");
    } finally { setStatsLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin") {
      toast.error("Kein Admin-Zugriff");
      navigate("/dashboard");
      return;
    }
    loadStats();
  }, [user, navigate, loadStats]);

  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="mx-auto max-w-md px-4 py-12">
        <Card className="border-red-500/30 bg-red-500/5 p-6 text-white">
          <AlertTriangle className="h-6 w-6 text-red-500" />
          <h1 className="mt-3 font-display text-2xl font-bold">Kein Zugriff</h1>
          <p className="mt-2 text-sm text-zinc-400">Diese Seite ist nur für Administratoren.</p>
          <Button asChild className="mt-4 bg-red-600 hover:bg-red-500"><Link to="/dashboard">Zum Dashboard</Link></Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600/15 text-red-500">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">CarHive · Admin</p>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Admin-Dashboard</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 -mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-1 border-b border-zinc-800">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            const active = tab === tt.id;
            return (
              <button
                key={tt.id}
                onClick={() => setTab(tt.id)}
                className={`relative flex shrink-0 items-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                  active ? "text-white" : "text-zinc-500 hover:text-zinc-300"
                }`}
                data-testid={`admin-tab-${tt.id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {tt.label}
                {active && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-red-500" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {tab === "overview" && <OverviewTab stats={stats} loading={statsLoading} onRefresh={loadStats} />}
        {tab === "users" && <UsersTab />}
        {tab === "listings" && <ListingsTab />}
        {tab === "searches" && <SearchesTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "dealers" && <DealersTab />}
        {tab === "push" && <PushTab />}
        {tab === "compare" && <CompareTab />}
        {tab === "email" && <EmailTab stats={stats} />}
      </div>
    </div>
  );
}
